/*
  Controller for the exposed filter pill bar (filter_type "pills").

  Markup contract (see snippets/facets-pill-bar.liquid):
  - [data-pill-toggle="<panel id>"]  button that opens/closes a panel
  - [data-pill-close]                closes the open panel (X, Apply)
  - [data-pill-backdrop]             full-screen layer behind an open panel
  - .facets-pill__panel              the panel itself; `hidden` when closed

  Buttons and panels are separate .js-filter elements, so facets.js can
  re-render the buttons' contents on every change without touching the open
  panel. Only attributes on the elements themselves (hidden, aria-expanded,
  is-open) carry the open state, and those survive an innerHTML swap.

  Window and document listeners are added on connect and removed on
  disconnect, so re-rendering the section (theme editor) does not leak them.
*/
class PillFilterBar extends HTMLElement {
  constructor() {
    super();
    this.openPanel = null;
    this.openButton = null;
    this.desktop = window.matchMedia('(min-width: 750px)');
    this.onResize = this.onResize.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener('click', this.onClick.bind(this));
  }

  connectedCallback() {
    this.bleed();
    requestAnimationFrame(() => this.bleed());
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.close();
  }

  get backdrop() {
    return this.querySelector('[data-pill-backdrop]');
  }

  get scroller() {
    return this.querySelector('.facets-pill-bar__scroller');
  }

  onResize() {
    this.bleed();
    this.position();
  }

  /* Escape closes the open panel wherever focus is (the sheet is modal on
     phones, and on desktop focus may have moved to the page). */
  onKeydown(event) {
    if (event.key === 'Escape' && this.openPanel) {
      event.preventDefault();
      this.close(true);
    }
  }

  /* Mobile: stretch the scrolling row to the screen edges by exactly the
     space available on each side. Measuring (rather than a fixed negative
     margin) keeps the row from ever being wider than the viewport, which
     would give the whole page a horizontal scroll. */
  bleed() {
    const scroller = this.scroller;
    if (!scroller) return;

    if (this.desktop.matches) {
      scroller.style.marginLeft = '';
      scroller.style.marginRight = '';
      scroller.style.paddingLeft = '';
      scroller.style.paddingRight = '';
      scroller.style.scrollPaddingLeft = '';
      return;
    }

    const rect = this.getBoundingClientRect();
    if (!rect.width) return;
    const viewportWidth = document.documentElement.clientWidth;
    const left = Math.max(0, Math.floor(rect.left));
    const right = Math.max(0, Math.floor(viewportWidth - rect.right));

    scroller.style.marginLeft = `-${left}px`;
    scroller.style.marginRight = `-${right}px`;
    scroller.style.paddingLeft = `${left}px`;
    scroller.style.paddingRight = `${right}px`;
    scroller.style.scrollPaddingLeft = `${left}px`;
  }

  onClick(event) {
    const toggle = event.target.closest('[data-pill-toggle]');
    if (toggle) {
      const panel = document.getElementById(toggle.dataset.pillToggle);
      if (!panel) return;
      panel === this.openPanel ? this.close(true) : this.open(toggle, panel);
      return;
    }

    if (event.target.closest('[data-pill-close], [data-pill-backdrop]')) {
      this.close(event.target.closest('[data-pill-close]') !== null);
    }
  }

  open(button, panel) {
    if (this.openPanel && this.openPanel !== panel) this.close();

    this.openPanel = panel;
    this.openButton = button;

    panel.hidden = false;
    panel.classList.add('is-open');
    button.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    this.classList.add('has-open-panel');
    if (this.backdrop) this.backdrop.hidden = false;
    document.addEventListener('keydown', this.onKeydown);

    this.position();

    // The phone bottom sheet is modal: keep Tab inside it (global.js helper).
    if (!this.desktop.matches && typeof trapFocus === 'function') {
      trapFocus(panel);
    } else {
      panel.focus({ preventScroll: true });
    }
  }

  close(returnFocus = false) {
    const panel = this.openPanel;
    const button = this.openButton;
    if (!panel) return;

    panel.hidden = true;
    panel.classList.remove('is-open');
    panel.style.left = '';
    panel.style.top = '';
    if (button) {
      button.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    }
    this.classList.remove('has-open-panel');
    if (this.backdrop) this.backdrop.hidden = true;
    document.body.classList.remove('facets-pill-sheet-open');
    document.removeEventListener('keydown', this.onKeydown);
    if (typeof removeTrapFocus === 'function') removeTrapFocus();

    this.openPanel = null;
    this.openButton = null;
    if (returnFocus && button) button.focus({ preventScroll: true });
  }

  /* Desktop: place the popover under its button, kept inside the bar's width.
     Mobile: the panel is a fixed bottom sheet, positioned by CSS alone. */
  position() {
    const panel = this.openPanel;
    const button = this.openButton;
    if (!panel || !button) return;

    if (!this.desktop.matches) {
      panel.style.left = '';
      panel.style.top = '';
      document.body.classList.add('facets-pill-sheet-open');
      return;
    }
    document.body.classList.remove('facets-pill-sheet-open');

    const barRect = this.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const maxLeft = Math.max(0, this.clientWidth - panel.offsetWidth);
    const left = Math.min(Math.max(0, buttonRect.left - barRect.left), maxLeft);
    panel.style.left = `${left}px`;
    panel.style.top = `${buttonRect.bottom - barRect.top + 12}px`;
  }
}

if (!customElements.get('pill-filter-bar')) {
  customElements.define('pill-filter-bar', PillFilterBar);
}
